import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawTable, drawHBar, drawClosingFooter, finalizeFooters, savePdf,
  COLORS, fmtGeneratedAt, severityCell, riskCell,
  type DocCtx, type KpiTile, type TableCell,
} from './pdfTemplate';

export type EntityType = 'user' | 'device' | 'ip' | 'domain' | 'url' | 'file_hash' | 'process' | 'mailbox' | 'cloud_app' | 'cve';
export type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';

export interface Entity360PdfSection {
  id: string;
  title: string;
  rows: Array<Record<string, unknown>>;
}

export interface Entity360PdfProfile {
  customerId: string;
  entityType: EntityType;
  entityValue: string;
  lookback: string;
  generatedAt: string;
  risk: { score: number; level: RiskLevel; reasons: string[] };
  summary: {
    incidents: number;
    alerts: number;
    vulnerabilities: number;
    signins: number;
    networkEvents: number;
    tiMatches: number;
    timelineEvents: number;
    relatedEntities: number;
  };
  sections: Entity360PdfSection[];
  relatedEntities: Array<{ type: string; value: string; count: number; sources: string[] }>;
  recommendedActions: Array<{ priority: string; title: string; details: string }>;
  diagnostics: string[];
}

export interface Entity360ReportInput {
  customerName: string;
  generatedAt: string;
  tz: string;
  profile: Entity360PdfProfile;
  aiAnalysisMarkdown?: string | null;
}

function text(value: unknown, max = 180): string {
  const raw = typeof value === 'string' ? value : JSON.stringify(value ?? '');
  const compact = raw.replace(/\s+/g, ' ').trim();
  return compact.length > max ? `${compact.slice(0, max - 1)}…` : compact;
}

function n(value: number | null | undefined): string {
  return Number.isFinite(value ?? NaN) ? Number(value).toLocaleString() : '0';
}

function drawMarkdown(ctx: DocCtx, markdown: string): void {
  const { pdf } = ctx;
  const lines = markdown.split(/\r?\n/).slice(0, 140);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { ctx.y += 2; continue; }
    if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
    if (line.startsWith('## ')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.slice(3), 14, ctx.y + 4);
      ctx.y += 7;
      continue;
    }
    if (line.startsWith('# ')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.slice(2), 14, ctx.y + 4);
      ctx.y += 8;
      continue;
    }
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    const printable = bullet ? `• ${bullet[1]}` : line.replace(/\*\*/g, '');
    pdf.setFont('helvetica', bullet ? 'normal' : 'normal');
    pdf.setFontSize(8.3);
    pdf.setTextColor(...COLORS.ink);
    const wrapped = pdf.splitTextToSize(printable, 182);
    for (const w of wrapped) {
      if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
      pdf.text(String(w), 14, ctx.y + 4);
      ctx.y += 4.2;
    }
  }
  ctx.y += 2;
}

function section(profile: Entity360PdfProfile, id: string): Entity360PdfSection | undefined {
  return profile.sections.find((s) => s.id === id);
}

function compactRows(rows: Array<Record<string, unknown>>, columns: string[], limit: number): TableCell[][] {
  return rows.slice(0, limit).map((row) => columns.map((col) => {
    if (/severity|risk|priority/i.test(col)) {
      const value = text(row[col], 40) || '-';
      return /risk|priority/i.test(col) ? riskCell(value) : severityCell(value);
    }
    return text(row[col], 120) || '-';
  }));
}

function drawSectionTable(
  ctx: DocCtx,
  title: string,
  rows: Array<Record<string, unknown>>,
  columns: string[],
  limit = 20,
  columnStyles?: Record<number, Record<string, unknown>>,
): void {
  drawSectionBanner(ctx, title, 'primary');
  if (!rows.length) {
    drawCallout(ctx, { tone: 'neutral', title: 'No rows available', body: 'No records were available for this section in the exported Entity 360 profile.' });
    return;
  }
  drawTable(ctx, {
    head: columns,
    body: compactRows(rows, columns, limit),
    // Without explicit widths, autotable's auto-sizing can produce
    // pathologically narrow columns on these wide (6-7 column) tables,
    // causing long Title/Details text to wrap into far more lines than the
    // row height accounts for -- the garbled/overlapping-text symptom.
    columnStyles: columnStyles ?? {},
    minRowHeight: 8,
  });
}

export async function exportEntity360ReportPdf(input: Entity360ReportInput): Promise<string> {
  await preloadPdfLogo();
  const profile = input.profile;
  const ctx = createReportPdf({
    title: 'Entity 360 Investigation Brief',
    customerName: input.customerName,
    periodLabel: `${profile.lookback} lookback`,
    generatedLabel: fmtGeneratedAt(input.generatedAt, input.tz),
  });

  drawSectionBanner(ctx, 'Entity Overview', 'primary');
  const tiles: KpiTile[] = [
    { value: String(profile.risk.score), label: `Risk Score (${profile.risk.level})`, tone: profile.risk.level === 'Critical' || profile.risk.level === 'High' ? 'danger' : profile.risk.level === 'Medium' ? 'warning' : 'success' },
    { value: n(profile.summary.incidents), label: 'Incidents', tone: 'info' },
    { value: n(profile.summary.alerts), label: 'Alerts', tone: 'warning' },
    { value: n(profile.summary.vulnerabilities), label: 'Vulnerabilities', tone: 'danger' },
    { value: n(profile.summary.signins), label: 'Sign-ins', tone: 'info' },
    { value: n(profile.summary.networkEvents), label: 'Network Events', tone: 'default' },
    { value: n(profile.summary.tiMatches), label: 'TI Matches', tone: 'danger' },
    { value: n(profile.summary.relatedEntities), label: 'Related Entities', tone: 'success' },
  ];
  drawKpiTiles(ctx, tiles);

  drawCallout(ctx, {
    tone: profile.risk.level === 'Critical' || profile.risk.level === 'High' ? 'critical' : profile.risk.level === 'Medium' ? 'warning' : 'success',
    title: `${profile.entityType.replace('_', ' ').toUpperCase()}: ${profile.entityValue}`,
    body: `Generated from Entity 360 telemetry with ${profile.summary.timelineEvents.toLocaleString()} timeline event(s). Risk level: ${profile.risk.level}.`,
  });

  drawSectionBanner(ctx, 'Risk Drivers', 'orange');
  for (const reason of profile.risk.reasons.slice(0, 8)) {
    drawCallout(ctx, { tone: 'info', title: 'Risk signal', body: reason });
  }

  if (input.aiAnalysisMarkdown) {
    drawSectionBanner(ctx, 'AI Entity Analysis', 'primary');
    drawMarkdown(ctx, input.aiAnalysisMarkdown);
  }

  if (profile.recommendedActions.length) {
    drawSectionTable(ctx, 'Recommended Actions', profile.recommendedActions.map((a) => ({ Priority: a.priority, Title: a.title, Details: a.details })), ['Priority', 'Title', 'Details'], 12, {
      0: { cellWidth: 22, halign: 'center' },
      1: { cellWidth: 45 },
      2: { cellWidth: 'auto' },
    });
  }

  if (profile.relatedEntities.length) {
    const relatedRows = profile.relatedEntities.slice(0, 20).map((entity) => ({
      Type: entity.type,
      Value: entity.value,
      Count: entity.count,
      Sources: entity.sources.join(', '),
    }));
    drawSectionTable(ctx, 'Top Related Entities', relatedRows, ['Type', 'Value', 'Count', 'Sources'], 20, {
      0: { cellWidth: 24 },
      1: { cellWidth: 45 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 'auto' },
    });
  }

  const timeline = section(profile, 'timeline')?.rows ?? [];
  drawSectionTable(ctx, 'Unified Timeline', timeline, ['TimeGenerated', 'EventType', 'Source', 'Title', 'Severity', 'Actor', 'Details'], 25, {
    0: { cellWidth: 24 },
    1: { cellWidth: 20 },
    2: { cellWidth: 18 },
    3: { cellWidth: 30 },
    4: { cellWidth: 16, halign: 'center' },
    5: { cellWidth: 22 },
    6: { cellWidth: 'auto' },
  });

  const vulnerabilities = section(profile, 'vulnerabilities')?.rows ?? [];
  if (vulnerabilities.length) {
    drawSectionTable(ctx, 'Top Vulnerability Exposure', vulnerabilities, ['CveId', 'VulnerabilitySeverityLevel', 'DeviceName', 'SoftwareVendor', 'SoftwareName', 'RecommendedSecurityUpdate'], 25, {
      0: { cellWidth: 24 },
      1: { cellWidth: 22, halign: 'center' },
      2: { cellWidth: 28 },
      3: { cellWidth: 26 },
      4: { cellWidth: 30 },
      5: { cellWidth: 'auto' },
    });
  }

  const alerts = section(profile, 'alerts')?.rows ?? [];
  if (alerts.length) {
    drawSectionTable(ctx, 'Top Alerts', alerts, ['TimeGenerated', 'AlertName', 'Severity', 'ProductName', 'CompromisedEntity', 'Description'], 20, {
      0: { cellWidth: 26 },
      1: { cellWidth: 32 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 24 },
      4: { cellWidth: 28 },
      5: { cellWidth: 'auto' },
    });
  }

  const incidents = section(profile, 'incidents')?.rows ?? [];
  if (incidents.length) {
    drawSectionTable(ctx, 'Linked Incidents', incidents, ['TimeGenerated', 'IncidentNumber', 'Title', 'Severity', 'Status', 'ProviderName', 'Owner'], 20, {
      0: { cellWidth: 24 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 34 },
      3: { cellWidth: 16, halign: 'center' },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 26 },
      6: { cellWidth: 'auto' },
    });
  }

  const topTimelineTypes = new Map<string, number>();
  for (const row of timeline) {
    const key = text(row.EventType || row.Source || 'Unknown', 40);
    topTimelineTypes.set(key, (topTimelineTypes.get(key) ?? 0) + 1);
  }
  const series = Array.from(topTimelineTypes.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([label, value]) => ({ label, value }));
  if (series.length) {
    drawSectionBanner(ctx, 'Timeline Event Mix', 'primary');
    drawHBar(ctx, { series });
  }

  if (profile.diagnostics.length) {
    drawSectionBanner(ctx, 'Diagnostics / Partial Data Warnings', 'orange');
    drawTable(ctx, { head: ['Warning'], body: profile.diagnostics.slice(0, 10).map((item) => [text(item, 260)]) });
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  return savePdf(ctx, `entity-360-${profile.entityType}-${profile.entityValue.replace(/[^a-z0-9._-]+/gi, '-')}`);
}
